import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

import {
  Loading,
  HomeHero,
  HomeSupport,
  HomeTicker,
  HomeClans,
  HomeDivisions,
  HomePlayoffs,
  HomeCalendar,
  HomeMiniLeaderboard,
} from "../components";
import { useNavContext } from "../context/nav_context";

import {
  api_v1_get_current_tournament,
  api_v3_get_tournament_by_id,
  api_v3_get_playoff_matches_by_tournament,
  api_v3_list_of_tournaments,
} from "../utils/constants";

const HomePage2024 = () => {
  const [divisionsInProgress, setDivisionsInProgress] = useState(false);
  const [divisionsError, setDivisionsError] = useState(false);
  const [divisionsCompleted, setDivisionsCompleted] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [tournament, setTournament] = useState({});
  const [currentTournamentId, setCurrentTournamentId] = useState("");
  const [isLoadingCurrentTournament, setIsLoadingCurrentTournament] = useState(true);

  const [playoffMatches, setPlayoffMatches] = useState([]);
  const [playoffInProgress, setPlayoffInProgress] = useState(false);
  const [playoffError, setPlayoffError] = useState(false);

  const { user } = useNavContext();
  const [isPrivateAdminView, setIsPrivateAdminView] = useState(false);
  const [privateTournaments, setPrivateTournaments] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [publicTournamentId, setPublicTournamentId] = useState("");

  useEffect(() => {
    const fetchCurrentTournament = async () => {
      try {
        setIsLoadingCurrentTournament(true);
        const currentResponse = await axios.get(api_v1_get_current_tournament());
        let targetTournamentId = "";
        let alreadySet = false;

        if (currentResponse.status === 200 && currentResponse.data.tournamentId) {
          targetTournamentId = currentResponse.data.tournamentId;
        }

        if (targetTournamentId) {
          try {
            const detailsResponse = await axios.get(api_v3_get_tournament_by_id(targetTournamentId));
            if (detailsResponse.status === 200) {
              const tournamentData = detailsResponse.data;
              if (tournamentData.status === "private") {
                if (user && user.isAdmin) {
                  setCurrentTournamentId(targetTournamentId);
                  setIsPrivateAdminView(true);
                  setIsPreviewMode(true);
                  alreadySet = true;
                }
              } else {
                setCurrentTournamentId(targetTournamentId);
                setPublicTournamentId(targetTournamentId);
                setIsPrivateAdminView(false);
                setIsLoadingCurrentTournament(false);
                return;
              }
            }
          } catch (err) {
            console.error("Error checking tournament status", err);
          }
        }

        const response = await axios.get(api_v3_list_of_tournaments());
        if (response.status === 200 && response.data) {
          const filteredTournaments = response.data.filter(
            (t) => t.status !== "private"
          );
          filteredTournaments.sort((a, b) => {
            const endDateA = a.endDate ? new Date(a.endDate).getTime() : 0;
            const endDateB = b.endDate ? new Date(b.endDate).getTime() : 0;
            if (endDateB !== endDateA) return endDateB - endDateA;
            const startDateA = a.startDate ? new Date(a.startDate).getTime() : 0;
            const startDateB = b.startDate ? new Date(b.startDate).getTime() : 0;
            return startDateB - startDateA;
          });
          if (filteredTournaments.length > 0) {
            if (!alreadySet) {
              setCurrentTournamentId(filteredTournaments[0].tournamentId);
              setIsPrivateAdminView(false);
            }
            setPublicTournamentId(filteredTournaments[0].tournamentId);
          }
        }
      } catch (error) {
        console.error("Error al obtener el torneo actual:", error);
      } finally {
        setIsLoadingCurrentTournament(false);
      }
    };
    fetchCurrentTournament();
  }, [user]);

  useEffect(() => {
    if (user && user.isAdmin) {
      const fetchPrivateTournaments = async () => {
        try {
          const response = await axios.get(api_v3_list_of_tournaments());
          if (response.status === 200 && response.data) {
            setPrivateTournaments(response.data.filter(t => t.status === "private"));
          }
        } catch (error) {
          console.error("Error fetching private tournaments:", error);
        }
      };
      fetchPrivateTournaments();
    } else {
      setPrivateTournaments([]);
    }
  }, [user]);

  const handlePreviewTournament = (tournamentId) => {
    setCurrentTournamentId(tournamentId);
    setIsPrivateAdminView(true);
    setIsPreviewMode(true);
  };

  const handleExitPreview = () => {
    if (publicTournamentId) {
      setCurrentTournamentId(publicTournamentId);
      setIsPrivateAdminView(false);
      setIsPreviewMode(false);
    }
  };

  useEffect(() => {
    const loadDivisions = async () => {
      if (!currentTournamentId) return;
      const url = api_v3_get_tournament_by_id(currentTournamentId);
      try {
        setDivisionsInProgress(true);
        const response = await axios.get(url);
        if (response.status === 200) {
          const divs = response.data.divisions.sort((a, b) =>
            a.sequence > b.sequence ? 1 : -1
          );
          setTournament(response.data);
          setDivisions(divs);
          setDivisionsInProgress(false);
          setDivisionsError(false);
          setDivisionsCompleted(true);
        } else {
          setDivisionsInProgress(false);
          setDivisionsError(true);
        }
      } catch (error) {
        setDivisionsInProgress(false);
        setDivisionsError(true);
      }
    };
    loadDivisions();
  }, [currentTournamentId]);

  useEffect(() => {
    const loadPlayoffs = async () => {
      if (!currentTournamentId) return;
      const url = api_v3_get_playoff_matches_by_tournament(currentTournamentId);
      try {
        setPlayoffInProgress(true);
        const response = await axios.get(url);
        if (response.status === 200) {
          setPlayoffMatches(response.data);
          setPlayoffInProgress(false);
          setPlayoffError(false);
        } else {
          setPlayoffInProgress(false);
          setPlayoffError(true);
        }
      } catch (error) {
        setPlayoffInProgress(false);
        setPlayoffError(true);
      }
    };
    if (
      tournament.status &&
      (isPreviewMode || !["private", "registers", "registers-closed", "on going"].includes(tournament.status))
    ) {
      loadPlayoffs();
    }
  }, [tournament, currentTournamentId]);

  const getInscripcionesDateStr = () => {
    try {
      if (!tournament || !tournament.periods) return "27 de Abril a las 20:00";
      const inscripcionesPeriod = tournament.periods.find((p) => p.name === "Inscripciones");
      if (!inscripcionesPeriod || !inscripcionesPeriod.endDate) return "27 de Abril a las 20:00";
      const date = new Date(inscripcionesPeriod.endDate);
      const day = date.getDate();
      const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
      const month = months[date.getMonth()];
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${day} de ${month} a las ${hours}:${minutes}`;
    } catch (e) {
      return "27 de Abril a las 20:00";
    }
  };

  if (isLoadingCurrentTournament) {
    return (
      <Canvas>
        <Loading />
      </Canvas>
    );
  }

  const showCompetition =
    isPreviewMode ||
    (tournament.status &&
      tournament.status !== "private" &&
      tournament.status !== "registers" &&
      tournament.status !== "registers-closed");

  const showPlayoffs =
    isPreviewMode ||
    (tournament.status &&
      tournament.status !== "private" &&
      tournament.status !== "registers" &&
      tournament.status !== "registers-closed" &&
      tournament.status !== "on going");

  const showCalendar =
    tournament &&
    tournament.startDate &&
    tournament.endDate &&
    tournament.milestones &&
    tournament.periods &&
    !divisionsError &&
    !divisionsInProgress;

  return (
    <Canvas className="v-noise">
      {/* Admin preview bar */}
      {user && user.isAdmin && privateTournaments.length > 0 && (
        <PreviewBar>
          <PreviewBarLeft>
            <PreviewBadge>VISTA PREVIA</PreviewBadge>
            <PreviewBarText>
              {isPreviewMode
                ? `Previsualizando: ${tournament.name || "torneo privado"}`
                : "Torneos privados disponibles:"}
            </PreviewBarText>
          </PreviewBarLeft>
          <PreviewBarRight>
            {privateTournaments.map((t) => (
              <PreviewPill
                key={t.tournamentId}
                $active={currentTournamentId === t.tournamentId}
                onClick={() => handlePreviewTournament(t.tournamentId)}
              >
                {t.name}
              </PreviewPill>
            ))}
            {isPreviewMode && (
              <ExitPreviewBtn onClick={handleExitPreview}>
                ← Vista pública
              </ExitPreviewBtn>
            )}
          </PreviewBarRight>
        </PreviewBar>
      )}

      {/* Registration banner */}
      {tournament.status === "registers" && (
        <Banner>
          <BannerText>
            INSCRIPCIONES ABIERTAS HASTA EL <strong>{getInscripcionesDateStr()}</strong>
          </BannerText>
          <Link to={`/register/tournament/${currentTournamentId}`}>
            <button className="v-btn-mint">INSCRÍBETE AQUÍ</button>
          </Link>
        </Banner>
      )}

      {/* Hero + Leaderboard top row */}
      <TopRow>
        <HeroCol>
          <HomeHero
            tournament={tournament}
            currentTournamentId={currentTournamentId}
            isPrivateAdminView={isPrivateAdminView}
          />
          <HomeSupport layout="horizontal" />
        </HeroCol>
        <LeaderboardCol>
          <HomeMiniLeaderboard />
        </LeaderboardCol>
      </TopRow>

      {/* Ticker: próximos partidos */}
      <SectionBlock>
        <HomeTicker tournamentId={currentTournamentId} />
      </SectionBlock>

      {/* Clans */}
      <SectionBlock>
        <HomeClans tournamentId={currentTournamentId} />
      </SectionBlock>

      {/* Divisiones */}
      {showCompetition && (
        <SectionBlock>
          {divisionsInProgress ? (
            <Loading />
          ) : divisionsCompleted && !divisionsError && !divisionsInProgress ? (
            <HomeDivisions divisions={divisions} tournamentName={tournament.name} />
          ) : null}
        </SectionBlock>
      )}

      {/* Playoffs */}
      {showPlayoffs && (
        <SectionBlock>
          {playoffInProgress ? (
            <Loading />
          ) : playoffError ? (
            <ErrorMsg>Ha ocurrido un error cargando los partidos de playoff.</ErrorMsg>
          ) : playoffMatches && playoffMatches.length > 0 ? (
            <HomePlayoffs matches={playoffMatches} />
          ) : null}
        </SectionBlock>
      )}

      {/* Calendar */}
      {showCalendar && (
        <SectionBlock>
          <HomeCalendar
            startDate={tournament.startDate}
            endDate={tournament.endDate}
            periods={tournament.periods}
            milestones={tournament.milestones}
            tournamentId={tournament.tournamentId || currentTournamentId}
            divisions={divisions}
          />
        </SectionBlock>
      )}

      <Spacer />
    </Canvas>
  );
};

const Canvas = styled.main`
  background: var(--v-canvas);
  min-height: 100vh;
  padding: 1.5rem;
  position: relative;

  @media (min-width: 1024px) {
    padding: 2.5rem;
  }
`;

const TopRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: stretch;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const HeroCol = styled.div`
  flex: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LeaderboardCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;

  @media (max-width: 1000px) {
    width: 100%;
  }
`;

const SectionBlock = styled.div`
  max-width: 1280px;
  margin: 2rem auto 0;

  @media (min-width: 1024px) {
    margin-top: 3rem;
  }
`;

const Banner = styled.div`
  max-width: 1280px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  background: linear-gradient(90deg, rgba(60, 255, 208, 0.06), rgba(82, 0, 255, 0.06));
  border: 1px solid rgba(60, 255, 208, 0.15);
  border-radius: 20px;
  padding: 1rem 1.5rem;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const BannerText = styled.span`
  font-family: var(--v-font-ui);
  font-size: 15px;
  font-weight: 400;
  color: var(--v-white);

  strong {
    color: var(--v-mint);
    font-weight: 600;
  }
`;

const PreviewBar = styled.div`
  max-width: 1280px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  background: linear-gradient(90deg, rgba(201, 160, 78, 0.08), rgba(82, 0, 255, 0.08));
  border: 1px solid var(--v-gold-dim);
  border-radius: 20px;
  padding: 0.85rem 1.5rem;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PreviewBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PreviewBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PreviewBadge = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.8px;
  color: var(--v-gold);
  border: 1px solid var(--v-gold-dim);
  border-radius: 4px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const PreviewBarText = styled.span`
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: 400;
  color: var(--v-secondary);

  @media (max-width: 600px) {
    font-size: 12px;
  }
`;

const PreviewPill = styled.button`
  padding: 0.35rem 0.85rem;
  border: 1px solid ${({ $active }) => ($active ? "var(--v-gold)" : "rgba(255, 255, 255, 0.08)")};
  background: ${({ $active }) => ($active ? "rgba(201, 160, 78, 0.12)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--v-gold-bright)" : "var(--v-secondary)")};
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--v-gold);
    color: var(--v-gold-bright);
  }
`;

const ExitPreviewBtn = styled.button`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  color: var(--v-secondary);
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 0.35rem 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--v-mint);
    border-color: var(--v-mint);
  }
`;

const ErrorMsg = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-secondary);
  text-align: center;
  padding: 2rem;
`;

const Spacer = styled.div`
  height: 4rem;
`;

export default HomePage2024;
